import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { stripHtml } from "../utils/html";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";
import { useIsMobile } from "../hooks/useIsMobile";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { pathFor } from "../app/paths";

gsap.registerPlugin(ScrollTrigger);

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";

interface BlogArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  image_url: string | null;
  created_at: string;
}

function formatDate(iso: string, locale: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(locale === "en" ? "en-GB" : `${locale}-${locale.toUpperCase()}`, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

/* ── Featured Article (first article, full-width) ── */
function FeaturedCard({
  article,
  locale,
  cfg,
  isMobile,
  t,
}: {
  article: BlogArticle;
  locale: string;
  cfg: ReturnType<typeof useSiteConfig>;
  isMobile: boolean;
  t: (k: string) => string;
}) {
  return (
    <Link
      to={pathFor("blog", locale, article.slug)}
      className="gs-blog-featured"
      style={{ textDecoration: "none", color: "inherit", display: "block", marginBottom: isMobile ? 48 : 64 }}
    >
      <article
        style={{
          display: isMobile ? "block" : "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr",
          gap: isMobile ? 20 : 48,
          alignItems: "center",
        }}
      >
        {/* Image */}
        <div
          className="img-zoom gs-blog-featured-img"
          style={{
            width: "100%",
            aspectRatio: isMobile ? "16/10" : "3/2",
            borderRadius: 8,
            overflow: "hidden",
            background: cfg.color_light_bg,
          }}
        >
          {article.image_url ? (
            <img
              src={resolveImageUrl(article.image_url)}
              alt={article.title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                transition: "transform .6s ease",
              }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke={cfg.color_muted}
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ opacity: 0.3 }}
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="gs-blog-featured-text" style={{ paddingTop: isMobile ? 0 : 0 }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: cfg.color_muted,
              margin: "0 0 12px",
            }}
          >
            {formatDate(article.created_at, locale)}
          </p>
          <h2
            style={{
              fontFamily: cfg.font_heading,
              fontSize: isMobile ? 24 : 32,
              fontWeight: 400,
              color: cfg.color_dark,
              margin: "0 0 16px",
              lineHeight: 1.25,
              letterSpacing: -0.2,
            }}
          >
            {article.title}
          </h2>
          {article.excerpt && (
            <p
              style={{
                fontSize: 15,
                color: "rgba(28,75,59,0.86)",
                lineHeight: 1.7,
                margin: "0 0 24px",
                maxWidth: 440,
              }}
            >
              {stripHtml(article.excerpt)}
            </p>
          )}
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: 1,
              textTransform: "uppercase",
              color: cfg.color_dark,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {t("blog.readMore")}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </article>
    </Link>
  );
}

/* ── Article Card ── */
function ArticleCard({
  article,
  locale,
  cfg,
  t,
}: {
  article: BlogArticle;
  locale: string;
  cfg: ReturnType<typeof useSiteConfig>;
  t: (k: string) => string;
}) {
  return (
    <Link
      to={pathFor("blog", locale, article.slug)}
      className="gs-article-card"
      style={{
        textDecoration: "none",
        color: "inherit",
        display: "block",
        transition: "transform 0.35s cubic-bezier(.4,0,.2,1)",
      }}
    >
      <article
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Cover image */}
        <div
          className="img-zoom"
          style={{
            width: "100%",
            aspectRatio: "4/3",
            borderRadius: 8,
            overflow: "hidden",
            background: cfg.color_light_bg,
          }}
        >
          {article.image_url ? (
            <img
              src={resolveImageUrl(article.image_url)}
              alt={article.title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                transition: "transform .6s ease",
              }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke={cfg.color_muted}
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ opacity: 0.3 }}
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </div>
          )}
        </div>

        {/* Content */}
        <div
          style={{
            paddingTop: 18,
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <p
            style={{
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              color: cfg.color_muted,
              margin: "0 0 10px",
              lineHeight: 1,
            }}
          >
            {formatDate(article.created_at, locale)}
          </p>

          <h3
            style={{
              fontFamily: cfg.font_heading,
              fontSize: 20,
              fontWeight: 400,
              color: cfg.color_dark,
              margin: "0 0 10px",
              lineHeight: 1.3,
            }}
          >
            {article.title}
          </h3>
          {article.excerpt && (
            <p
              style={{
                fontSize: 14,
                color: "rgba(28,75,59,0.86)",
                lineHeight: 1.65,
                margin: "0 0 auto",
                paddingBottom: 16,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {stripHtml(article.excerpt)}
            </p>
          )}
          <span
            style={{
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: 1,
              textTransform: "uppercase",
              color: cfg.color_dark,
              marginTop: article.excerpt ? 0 : "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {t("blog.readMore")}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </article>
    </Link>
  );
}

/* ── Main Blog Listing ── */
export function Blog() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const cfg = useSiteConfig();
  const isMobile = useIsMobile();
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/shop/blog-articles?lang=${locale || "it"}`)
      .then((r) => r.json())
      .then(setArticles)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [locale]);

  useLayoutEffect(() => {
    if (loading) return;

    const ctx = gsap.context(() => {
      /* ── 1. Hero stagger fade-up ── */
      const heroEls = gsap.utils.toArray<HTMLElement>(".gs-blog-hero > *");
      gsap.set(heroEls, { opacity: 0, y: 32 });
      const heroTl = gsap.timeline({ defaults: { ease: "expo.out", duration: 1.2 } });
      heroEls.forEach((el, i) => {
        heroTl.to(el, { opacity: 1, y: 0 }, i * 0.15);
      });

      /* ── 2. Featured article ── */
      gsap.from(".gs-blog-featured-img", {
        x: -60,
        opacity: 0,
        duration: 1.3,
        ease: "expo.out",
        scrollTrigger: { trigger: ".gs-blog-featured", start: "top 80%" },
      });
      gsap.from(".gs-blog-featured-text", {
        x: 60,
        opacity: 0,
        duration: 1.3,
        ease: "expo.out",
        scrollTrigger: { trigger: ".gs-blog-featured", start: "top 80%" },
      });

      /* ── 3. Divider line ── */
      gsap.from(".gs-blog-divider", {
        scaleX: 0,
        transformOrigin: "left center",
        duration: 1,
        ease: "expo.out",
        scrollTrigger: { trigger: ".gs-blog-divider", start: "top 85%" },
      });

      /* ── 4. Article cards batch ── */
      ScrollTrigger.batch(".gs-article-card", {
        start: "top 88%",
        onEnter: (batch) =>
          gsap.from(batch, {
            y: 40,
            opacity: 0,
            duration: 1,
            ease: "expo.out",
            stagger: 0.1,
          }),
        once: true,
      });
    }, containerRef);

    return () => ctx.revert();
  }, [loading]);

  const featured = articles.length > 0 ? articles[0] : null;
  const rest = articles.slice(1);

  return (
    <div ref={containerRef}>
      {/* ── Hover lift style for article cards ── */}
      <style>{`
        .gs-article-card:hover {
          transform: translateY(-4px) !important;
        }
      `}</style>

      {/* Hero */}
      <section
        style={{
          background: cfg.color_dark,
          padding: isMobile ? "80px 24px" : "120px 48px",
          textAlign: "center",
        }}
      >
        <div className="gs-blog-hero" style={{ maxWidth: 640, margin: "0 auto" }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: cfg.color_accent,
              margin: "0 0 16px",
            }}
          >
            {cfg.brand_name}
          </p>
          <h1
            style={{
              fontFamily: cfg.font_heading,
              fontSize: isMobile ? 36 : 52,
              fontWeight: 400,
              color: "#fff",
              margin: "0 0 16px",
              lineHeight: 1.1,
              letterSpacing: -0.3,
            }}
          >
            {t("blog.title")}
          </h1>
          <p
            style={{
              fontSize: isMobile ? 14 : 16,
              color: "#fff",
              maxWidth: 480,
              margin: "0 auto",
              lineHeight: 1.7,
            }}
          >
            {t("blog.description")}
          </p>
        </div>
      </section>

      {/* Content */}
      <section
        style={{
          background: cfg.color_body_bg,
          padding: isMobile ? "56px 24px" : "96px 48px",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 80 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  border: "2px solid rgba(28,75,59,0.89)",
                  borderTopColor: cfg.color_primary,
                  borderRadius: "50%",
                  animation: "spin .8s linear infinite",
                  margin: "0 auto",
                }}
              />
            </div>
          ) : articles.length === 0 ? (
            <p style={{ textAlign: "center", color: "rgba(28,75,59,0.86)", padding: 80 }}>
              {t("blog.noArticles")}
            </p>
          ) : (
            <>
              {/* Featured article */}
              {featured && (
                <>
                  <FeaturedCard
                    article={featured}
                    locale={locale || "it"}
                    cfg={cfg}
                    isMobile={isMobile}
                    t={t}
                  />
                  {rest.length > 0 && (
                    <div
                      className="gs-blog-divider"
                      style={{
                        height: 1,
                        background: "rgba(28,75,59,0.89)",
                        marginBottom: isMobile ? 48 : 64,
                      }}
                    />
                  )}
                </>
              )}

              {/* Rest of articles */}
              {rest.length > 0 && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? "260px" : "320px"}, 1fr))`,
                    gap: isMobile ? 36 : 48,
                  }}
                >
                  {rest.map((article) => (
                    <ArticleCard
                      key={article.id}
                      article={article}
                      locale={locale || "it"}
                      cfg={cfg}
                      t={t}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
